import { useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import api from "@/lib/api";
import { Printer, Cloud, CheckCircle2, AlertTriangle, RefreshCw, Save, Send } from "lucide-react";
import { toast } from "sonner";

const TEAL = "#0f766e";

export default function PrintCenter() {
  const [settings, setSettings] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, p] = await Promise.all([api.get("/print/settings"), api.get("/print/printers")]);
      setSettings(s.data);
      setConfigured(p.data.configured && s.data.configured);
      setPrinters(p.data.printers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach the print server");
      setConfigured(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const pickA4 = (id) => {
    const p = printers.find((x) => String(x.id) === String(id));
    setField({ a4_printer_id: p ? p.id : null, a4_printer_name: p ? `${p.name} · ${p.computer || ""}` : null });
  };
  const pickLabel = (id) => {
    const p = printers.find((x) => String(x.id) === String(id));
    setField({ label_printer_id: p ? p.id : null, label_printer_name: p ? `${p.name} · ${p.computer || ""}` : null });
  };
  const pickPicklist = (id) => {
    const p = printers.find((x) => String(x.id) === String(id));
    setField({ picklist_printer_id: p ? p.id : null, picklist_printer_name: p ? `${p.name} · ${p.computer || ""}` : null });
  };
  const pickSo = (id) => {
    const p = printers.find((x) => String(x.id) === String(id));
    setField({ so_printer_id: p ? p.id : null, so_printer_name: p ? `${p.name} · ${p.computer || ""}` : null });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/print/settings", {
        a4_printer_id: settings.a4_printer_id, a4_printer_name: settings.a4_printer_name,
        label_printer_id: settings.label_printer_id, label_printer_name: settings.label_printer_name,
        picklist_printer_id: settings.picklist_printer_id, picklist_printer_name: settings.picklist_printer_name,
        so_printer_id: settings.so_printer_id, so_printer_name: settings.so_printer_name,
        auto_print_gi: !!settings.auto_print_gi,
        auto_print_wave_assign: !!settings.auto_print_wave_assign,
      });
      toast.success("Print settings saved");
    } catch (e) { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const testPrint = async (printerId, kind) => {
    if (!printerId) { toast.error("Select a printer first"); return; }
    try {
      await api.post("/print/send", { doc_type: "label", kind: "bin", ref: `TEST-${kind}`, printer_id: printerId, copies: 1 });
      toast.success("Test label sent to printer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test print failed"); }
  };

  if (loading || !settings) return <Loader label="Loading print centre…" />;

  const online = printers.filter((p) => p.state === "online").length;

  return (
    <div>
      <PageHeader title="Print Centre" subtitle="Cloud relay to your Windows print server (PrintNode). Choose default printers and auto-print delivery documents on goods issue." icon={Printer} accent={TEAL}>
        <button data-testid="pc-refresh" onClick={load} disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg border border-[#c8cfda] px-4 py-2 text-sm font-semibold text-[#354a5f] hover:border-[#0f766e] disabled:opacity-50">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh Printers</button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Relay" value={configured ? "Connected" : "Not linked"} accent={configured ? "#10B981" : "#EF4444"} />
        <KpiCard label="Printers" value={printers.length} accent={TEAL} />
        <KpiCard label="Online" value={online} accent="#0284C7" />
        <KpiCard label="Auto-print on GI" value={settings.auto_print_gi ? "On" : "Off"} accent={settings.auto_print_gi ? "#10B981" : "#6a6d70"} />
      </div>

      {!configured && (
        <div data-testid="pc-not-configured" className="mb-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>The PrintNode cloud relay is not reachable. Make sure the PrintNode client is installed and signed in on the Windows print-server PC, the target printers are online, and the API key is valid. Click <b>Refresh Printers</b> once the client is running.</div>
        </div>
      )}

      {configured && printers.length === 0 && (
        <div data-testid="pc-no-printers" className="mb-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>Relay connected, but no printers were found. Install and sign in to the PrintNode client on the PC connected to your A4 and Zebra label printers, then refresh.</div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Default printers */}
        <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Cloud size={15} /> Default Printers</h3>

          <label className="mb-1 block text-xs font-semibold text-[#354a5f]">A4 documents (Delivery Notes, ad-hoc Pick Lists)</label>
          <div className="mb-4 flex items-center gap-2">
            <select data-testid="pc-a4-select" value={settings.a4_printer_id || ""} onChange={(e) => pickA4(e.target.value)}
              className="flex-1 rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
              <option value="">Select A4 printer…</option>
              {printers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.computer || "PC"} ({p.state})</option>)}
            </select>
            <button data-testid="pc-a4-test" onClick={() => testPrint(settings.a4_printer_id, "A4")}
              className="inline-flex items-center gap-1 rounded-lg border border-[#c8cfda] px-3 py-2 text-xs font-semibold text-[#0f766e] hover:border-[#0f766e]"><Send size={13} /> Test</button>
          </div>

          <label className="mb-1 block text-xs font-semibold text-[#354a5f]">Pick lists (wave sheets for pickers)</label>
          <div className="mb-4 flex items-center gap-2">
            <select data-testid="pc-picklist-select" value={settings.picklist_printer_id || ""} onChange={(e) => pickPicklist(e.target.value)}
              className="flex-1 rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
              <option value="">Select pick-list printer (defaults to A4)…</option>
              {printers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.computer || "PC"} ({p.state})</option>)}
            </select>
            <button data-testid="pc-picklist-test" onClick={() => testPrint(settings.picklist_printer_id, "PL")}
              className="inline-flex items-center gap-1 rounded-lg border border-[#c8cfda] px-3 py-2 text-xs font-semibold text-[#0f766e] hover:border-[#0f766e]"><Send size={13} /> Test</button>
          </div>

          <label className="mb-1 block text-xs font-semibold text-[#354a5f]">Sales orders / order confirmations</label>
          <div className="mb-4 flex items-center gap-2">
            <select data-testid="pc-so-select" value={settings.so_printer_id || ""} onChange={(e) => pickSo(e.target.value)}
              className="flex-1 rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
              <option value="">Select sales-order printer (defaults to A4)…</option>
              {printers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.computer || "PC"} ({p.state})</option>)}
            </select>
            <button data-testid="pc-so-test" onClick={() => testPrint(settings.so_printer_id, "SO")}
              className="inline-flex items-center gap-1 rounded-lg border border-[#c8cfda] px-3 py-2 text-xs font-semibold text-[#0f766e] hover:border-[#0f766e]"><Send size={13} /> Test</button>
          </div>

          <label className="mb-1 block text-xs font-semibold text-[#354a5f]">4×6 thermal labels (SSCC, Box, Bin, Material)</label>
          <div className="flex items-center gap-2">
            <select data-testid="pc-label-select" value={settings.label_printer_id || ""} onChange={(e) => pickLabel(e.target.value)}
              className="flex-1 rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
              <option value="">Select label printer…</option>
              {printers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.computer || "PC"} ({p.state})</option>)}
            </select>
            <button data-testid="pc-label-test" onClick={() => testPrint(settings.label_printer_id, "LBL")}
              className="inline-flex items-center gap-1 rounded-lg border border-[#c8cfda] px-3 py-2 text-xs font-semibold text-[#0f766e] hover:border-[#0f766e]"><Send size={13} /> Test</button>
          </div>
        </div>

        {/* Automation */}
        <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><CheckCircle2 size={15} /> Automation</h3>
          <button data-testid="pc-auto-toggle" onClick={() => setField({ auto_print_gi: !settings.auto_print_gi })}
            className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition ${settings.auto_print_gi ? "border-[#0f766e] bg-[#0f766e]/5" : "border-[#e5e9f0] hover:border-[#0f766e]/40"}`}>
            <div>
              <div className="text-sm font-semibold text-[#32363a]">Auto-print on Goods Issue</div>
              <div className="text-[11px] text-[#6a6d70]">When a delivery is goods-issued, automatically send the A4 delivery note and SSCC pallet label to the default printers above.</div>
            </div>
            <span className={`ml-3 inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition ${settings.auto_print_gi ? "bg-[#0f766e]" : "bg-[#c8cfda]"}`}>
              <span className={`h-5 w-5 rounded-full bg-white transition ${settings.auto_print_gi ? "translate-x-5" : ""}`} /></span>
          </button>

          <button data-testid="pc-auto-wave-toggle" onClick={() => setField({ auto_print_wave_assign: !settings.auto_print_wave_assign })}
            className={`mt-3 flex w-full items-center justify-between rounded-lg border p-4 text-left transition ${settings.auto_print_wave_assign ? "border-[#0f766e] bg-[#0f766e]/5" : "border-[#e5e9f0] hover:border-[#0f766e]/40"}`}>
            <div>
              <div className="text-sm font-semibold text-[#32363a]">Auto-print Pick List on wave assignment</div>
              <div className="text-[11px] text-[#6a6d70]">When a pick wave is assigned to a picker, automatically print the wave's pick list to the pick-list printer above (falls back to the A4 printer).</div>
            </div>
            <span className={`ml-3 inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition ${settings.auto_print_wave_assign ? "bg-[#0f766e]" : "bg-[#c8cfda]"}`}>
              <span className={`h-5 w-5 rounded-full bg-white transition ${settings.auto_print_wave_assign ? "translate-x-5" : ""}`} /></span>
          </button>
          <p className="mt-3 text-[11px] text-[#89919a]">Auto-printing needs the relevant default printers set and online. Printing never blocks goods-issue or wave assignment.</p>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button data-testid="pc-save" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0f766e] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0b5f58] disabled:opacity-50">
          <Save size={16} /> {saving ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
}
